import React, { useEffect, useState, useCallback } from 'react';
import { userAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ROLE_CFG = {
  admin:      { label: 'Admin',        color: '#dc2626', bg: '#fee2e2',  desc: 'Toàn quyền hệ thống' },
  hr_manager: { label: 'HR Manager',   color: '#7c3aed', bg: '#ede9fe',  desc: 'Quản lý nhân sự' },
  hr_staff:   { label: 'HR Staff',     color: '#2563eb', bg: '#dbeafe',  desc: 'Nhân viên HR' },
  manager:    { label: 'Manager',      color: '#0891b2', bg: '#cffafe',  desc: 'Quản lý phòng ban' },
  employee:   { label: 'Nhân viên',    color: '#64748b', bg: '#f1f5f9',  desc: 'Nhân viên thường' },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CFG[role] || ROLE_CFG.employee;
  return (
    <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:20,
      fontSize:11, fontWeight:700, color:cfg.color, background:cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('vi-VN', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function UserManagementPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [roleModal, setRoleModal]     = useState(null); // { user }
  const [pwModal, setPwModal]         = useState(null); // { user }
  const [createModal, setCreateModal] = useState(false);

  const [newRole, setNewRole]   = useState('');
  const [newPw, setNewPw]       = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [modalErr, setModalErr] = useState('');
  const [saving, setSaving]     = useState(false);

  const [createForm, setCreateForm] = useState({ employee_id:'', email:'', password:'', role:'employee' });
  const [createErr, setCreateErr]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, eRes] = await Promise.all([
        userAPI.list(),
        employeeAPI.list({ limit: 200 }),
      ]);
      setUsers(uRes.data.data);
      setEmployees(eRes.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // Filter
  const filtered = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterStatus === 'active' && !u.is_active) return false;
    if (filterStatus === 'inactive' && u.is_active) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(u.full_name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.employee_code?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Grouped by role for the stat cards
  const counts = Object.keys(ROLE_CFG).reduce((acc, r) => {
    acc[r] = users.filter(u => u.role === r).length;
    return acc;
  }, {});

  const handleToggle = async (u) => {
    if (u.id === me.id) return;
    const action = u.is_active ? 'vô hiệu hoá' : 'kích hoạt';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} tài khoản của ${u.full_name}?`)) return;
    try {
      await userAPI.toggleActive(u.id);
      setUsers(list => list.map(x => x.id === u.id ? { ...x, is_active: !x.is_active } : x));
      flash(`Đã ${action} tài khoản ${u.full_name}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Lỗi cập nhật');
    }
  };

  const handleRoleSave = async () => {
    if (!newRole) { setModalErr('Chọn quyền mới'); return; }
    if (newRole === roleModal.role) { setModalErr('Quyền không thay đổi'); return; }
    setSaving(true); setModalErr('');
    try {
      await userAPI.updateRole(roleModal.id, newRole);
      setUsers(list => list.map(x => x.id === roleModal.id ? { ...x, role: newRole } : x));
      setRoleModal(null);
      flash(`Đã cập nhật quyền cho ${roleModal.full_name}`);
    } catch (e) {
      setModalErr(e.response?.data?.message || 'Lỗi lưu');
    } finally {
      setSaving(false);
    }
  };

  const handlePwSave = async () => {
    if (!newPw || newPw.length < 6) { setModalErr('Mật khẩu tối thiểu 6 ký tự'); return; }
    setSaving(true); setModalErr('');
    try {
      await userAPI.resetPassword(pwModal.id, newPw);
      setPwModal(null); setNewPw('');
      flash(`Đã đặt lại mật khẩu cho ${pwModal.full_name}`);
    } catch (e) {
      setModalErr(e.response?.data?.message || 'Lỗi lưu');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setCreateErr('');
    if (!createForm.email.trim()) { setCreateErr('Nhập email'); return; }
    if (!createForm.password || createForm.password.length < 6) { setCreateErr('Mật khẩu tối thiểu 6 ký tự'); return; }
    setSaving(true);
    try {
      await userAPI.create(createForm);
      setCreateModal(false);
      setCreateForm({ employee_id:'', email:'', password:'', role:'employee' });
      load();
      flash('Đã tạo tài khoản thành công');
    } catch (e) {
      setCreateErr(e.response?.data?.message || 'Lỗi tạo tài khoản');
    } finally {
      setSaving(false);
    }
  };

  // Employees without a user account
  const userEmpIds = new Set(users.map(u => u.employee_id).filter(Boolean));
  const unlinkedEmps = employees.filter(e => !userEmpIds.has(e.id));

  const inp = { padding:'8px 12px', borderRadius:7, border:'1px solid #e2e8f0', fontSize:13, width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:3 };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:24, fontWeight:700, color:'#1e293b', margin:0 }}>Phân quyền tài khoản</h1>
        <p style={{ fontSize:14, color:'#64748b', margin:'4px 0 0' }}>Quản lý quyền truy cập hệ thống cho toàn bộ nhân viên</p>
      </div>

      {/* Role stat cards */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        {Object.entries(ROLE_CFG).map(([role, cfg]) => (
          <div key={role} style={{ background:'#fff', borderRadius:12, padding:'14px 20px', flex:1, minWidth:120,
            boxShadow:'0 1px 4px rgba(0,0,0,0.07)', borderLeft:`4px solid ${cfg.color}`,
            cursor:'pointer', transition:'box-shadow 0.15s',
          }}
            onClick={() => setFilterRole(filterRole === role ? '' : role)}
            onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.12)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.07)'}
          >
            <div style={{ fontSize:24, fontWeight:800, color:cfg.color }}>{counts[role] || 0}</div>
            <div style={{ fontSize:12, fontWeight:600, color:cfg.color }}>{cfg.label}</div>
            <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>{cfg.desc}</div>
            {filterRole === role && <div style={{ fontSize:10, color:cfg.color, marginTop:4, fontWeight:700 }}>▲ đang lọc</div>}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:18, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Tìm theo tên, email, mã NV..."
            style={{ ...inp, width:'auto', flexGrow:1, minWidth:220 }} />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...inp, width:'auto', minWidth:150 }}>
            <option value="">Tất cả quyền</option>
            {Object.entries(ROLE_CFG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width:'auto', minWidth:140 }}>
            <option value="">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Đã vô hiệu hoá</option>
          </select>
          <button onClick={() => { setCreateForm({ employee_id:'', email:'', password:'', role:'employee' }); setCreateErr(''); setCreateModal(true); }}
            style={{ marginLeft:'auto', background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:700, fontSize:13 }}>
            + Tạo tài khoản
          </button>
        </div>
      </div>

      {(error || success) && (
        <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:14, fontSize:13,
          background: error ? '#fee2e2' : '#dcfce7',
          color: error ? '#dc2626' : '#16a34a' }}>
          {error || success}
          {error && <button onClick={() => setError('')} style={{ marginLeft:12, background:'none', border:'none', cursor:'pointer', color:'inherit', fontWeight:700 }}>×</button>}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#64748b' }}>Đang tải...</div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'#f8fafc' }}>
              <tr>
                {['Nhân viên','Email','Quyền','Trạng thái','Đăng nhập cuối','Thao tác'].map(h => (
                  <th key={h} style={{ padding:'11px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#64748b', borderBottom:'2px solid #f1f5f9', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:14 }}>Không tìm thấy tài khoản nào</td></tr>
              ) : filtered.map(u => (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}
                  onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  {/* Employee info */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0,
                        background: u.is_active ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : '#e2e8f0',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        color:'#fff', fontWeight:700, fontSize:14 }}>
                        {u.full_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13, color:'#1e293b' }}>{u.full_name || '(Chưa liên kết)'}</div>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>
                          {u.employee_code ? `${u.employee_code} · ` : ''}{u.department_name || '—'}
                          {u.position_title ? ` · ${u.position_title}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Email */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc', fontSize:13, color:'#475569' }}>{u.email}</td>
                  {/* Role */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc' }}><RoleBadge role={u.role} /></td>
                  {/* Status */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, fontWeight:600,
                      color: u.is_active ? '#16a34a' : '#9ca3af' }}>
                      <span style={{ width:7, height:7, borderRadius:'50%', background: u.is_active ? '#16a34a' : '#9ca3af', display:'inline-block' }} />
                      {u.is_active ? 'Hoạt động' : 'Vô hiệu'}
                    </span>
                  </td>
                  {/* Last login */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtDate(u.last_login)}</td>
                  {/* Actions */}
                  <td style={{ padding:'12px 16px', borderBottom:'1px solid #f8fafc', whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      {/* Change role */}
                      <button disabled={u.id === me.id}
                        onClick={() => { setRoleModal(u); setNewRole(u.role); setModalErr(''); }}
                        style={{ padding:'5px 11px', borderRadius:6, border:'1px solid #e2e8f0', background:'#fff',
                          cursor: u.id === me.id ? 'not-allowed' : 'pointer',
                          fontSize:12, color:'#6366f1', fontWeight:600,
                          opacity: u.id === me.id ? 0.4 : 1 }}>
                        🔑 Phân quyền
                      </button>
                      {/* Reset password */}
                      <button onClick={() => { setPwModal(u); setNewPw(''); setShowPw(false); setModalErr(''); }}
                        style={{ padding:'5px 11px', borderRadius:6, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:12, color:'#0891b2', fontWeight:600 }}>
                        🔒 Đặt lại mật khẩu
                      </button>
                      {/* Toggle active */}
                      <button disabled={u.id === me.id}
                        onClick={() => handleToggle(u)}
                        style={{ padding:'5px 11px', borderRadius:6,
                          border: u.is_active ? '1px solid #fca5a5' : '1px solid #86efac',
                          background:'#fff',
                          cursor: u.id === me.id ? 'not-allowed' : 'pointer',
                          fontSize:12,
                          color: u.is_active ? '#dc2626' : '#16a34a',
                          fontWeight:600,
                          opacity: u.id === me.id ? 0.4 : 1 }}>
                        {u.is_active ? '⊘ Vô hiệu hoá' : '✓ Kích hoạt'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding:'10px 16px', borderTop:'1px solid #f1f5f9', fontSize:12, color:'#94a3b8' }}>
            Hiển thị {filtered.length} / {users.length} tài khoản
          </div>
        </div>
      )}

      {/* ── Role modal ── */}
      {roleModal && (
        <Modal title="Thay đổi quyền truy cập" onClose={() => setRoleModal(null)}>
          <div style={{ marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#f8fafc', borderRadius:10, marginBottom:16 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#4f46e5)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700 }}>{roleModal.full_name?.charAt(0)}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{roleModal.full_name}</div>
                <div style={{ fontSize:12, color:'#94a3b8' }}>{roleModal.email}</div>
              </div>
              <div style={{ marginLeft:'auto' }}><RoleBadge role={roleModal.role} /></div>
            </div>
            <label style={lbl}>Quyền mới *</label>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {Object.entries(ROLE_CFG).map(([role, cfg]) => (
                <label key={role} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                  borderRadius:9, border:`2px solid ${newRole === role ? cfg.color : '#e2e8f0'}`,
                  background: newRole === role ? cfg.bg : '#fff',
                  cursor:'pointer', transition:'all 0.15s',
                }}>
                  <input type="radio" name="role" value={role} checked={newRole === role}
                    onChange={() => setNewRole(role)} style={{ accentColor: cfg.color }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color: newRole === role ? cfg.color : '#1e293b' }}>{cfg.label}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>{cfg.desc}</div>
                  </div>
                  {newRole === role && <span style={{ color:cfg.color, fontWeight:700 }}>✓</span>}
                </label>
              ))}
            </div>
          </div>
          {modalErr && <div style={{ background:'#fee2e2', color:'#dc2626', padding:'8px 12px', borderRadius:6, fontSize:12, marginBottom:10 }}>{modalErr}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={() => setRoleModal(null)} style={{ padding:'8px 20px', borderRadius:7, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13 }}>Huỷ</button>
            <button onClick={handleRoleSave} disabled={saving} style={{ padding:'8px 20px', borderRadius:7, border:'none', background: saving?'#a5b4fc':'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', cursor: saving?'not-allowed':'pointer', fontWeight:700, fontSize:13 }}>
              {saving ? 'Đang lưu...' : 'Xác nhận phân quyền'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Password modal ── */}
      {pwModal && (
        <Modal title="Đặt lại mật khẩu" onClose={() => setPwModal(null)}>
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#f8fafc', borderRadius:10, marginBottom:16 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#4f46e5)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700 }}>{pwModal.full_name?.charAt(0)}</div>
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>{pwModal.full_name}</div>
              <div style={{ fontSize:12, color:'#94a3b8' }}>{pwModal.email}</div>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Mật khẩu mới *</label>
            <div style={{ position:'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder="Tối thiểu 6 ký tự"
                style={{ ...inp, paddingRight:40 }} />
              <button onClick={() => setShowPw(s => !s)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:14 }}>
                {showPw ? '🙈' : '👁'}
              </button>
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Mật khẩu sẽ được thay đổi ngay lập tức</div>
          </div>
          {modalErr && <div style={{ background:'#fee2e2', color:'#dc2626', padding:'8px 12px', borderRadius:6, fontSize:12, marginBottom:10 }}>{modalErr}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={() => setPwModal(null)} style={{ padding:'8px 20px', borderRadius:7, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13 }}>Huỷ</button>
            <button onClick={handlePwSave} disabled={saving} style={{ padding:'8px 20px', borderRadius:7, border:'none', background: saving?'#a5b4fc':'linear-gradient(135deg,#0891b2,#0e7490)', color:'#fff', cursor: saving?'not-allowed':'pointer', fontWeight:700, fontSize:13 }}>
              {saving ? 'Đang lưu...' : 'Đặt lại mật khẩu'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Create account modal ── */}
      {createModal && (
        <Modal title="Tạo tài khoản mới" onClose={() => setCreateModal(false)}>
          <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
            <div>
              <label style={lbl}>Liên kết nhân viên</label>
              <select style={inp} value={createForm.employee_id} onChange={e => {
                const emp = employees.find(x => x.id == e.target.value);
                setCreateForm(f => ({ ...f, employee_id: e.target.value, email: emp?.email || f.email }));
              }}>
                <option value="">— Không liên kết —</option>
                {unlinkedEmps.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
              </select>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>Chỉ hiển thị nhân viên chưa có tài khoản</div>
            </div>
            <div>
              <label style={lbl}>Email đăng nhập *</label>
              <input style={inp} value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="email@company.com" />
            </div>
            <div>
              <label style={lbl}>Mật khẩu *</label>
              <input type="password" style={inp} value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Tối thiểu 6 ký tự" />
            </div>
            <div>
              <label style={lbl}>Phân quyền *</label>
              <select style={inp} value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}>
                {Object.entries(ROLE_CFG).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.desc}</option>)}
              </select>
            </div>
            {createErr && <div style={{ background:'#fee2e2', color:'#dc2626', padding:'8px 12px', borderRadius:6, fontSize:12 }}>{createErr}</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
              <button onClick={() => setCreateModal(false)} style={{ padding:'8px 20px', borderRadius:7, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13 }}>Huỷ</button>
              <button onClick={handleCreate} disabled={saving} style={{ padding:'8px 20px', borderRadius:7, border:'none', background: saving?'#a5b4fc':'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', cursor: saving?'not-allowed':'pointer', fontWeight:700, fontSize:13 }}>
                {saving ? 'Đang tạo...' : 'Tạo tài khoản'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:500, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#fff' }}>
          <div style={{ fontSize:16, fontWeight:700, color:'#1e293b' }}>{title}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#94a3b8' }}>×</button>
        </div>
        <div style={{ padding:'18px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
